import { isNil } from 'lodash';
import { KeyboardEvent, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@shared/components/Button';
import { Menu } from '@shared/components/Menu';
import { useConfirm } from '@shared/components/Modal';
import { useToast } from '@shared/components/Toast';
import { View } from '@shared/components/View';
import { LoadingIndicator } from '@shared/components/loading';
import { useDeleteEntitiesMutation } from '@shared/scenes/cards/entityBulkActions/useDeleteWorkflowEntities';
import { CardFormTypeContext } from '@shared/scenes/entity/cardFormTypeContext';
import { EntityForm } from '@shared/scenes/entity/components/EntityForm';
import { FormScrollView } from '@shared/scenes/entity/components/FormScrollView';
import { FormScrollProvider } from '@shared/scenes/entity/context';
import { useCreateCardEntity } from '@shared/scenes/entity/create/hooks/useCreateCardEntity';
import { CardEntityError } from '@shared/scenes/entity/create/hooks/useFormErrors';
import { useEditCardEntity } from '@shared/scenes/entity/edit/hooks/useEditCardEntity';
import { FieldRecord, UpdateField } from '@shared/scenes/entity/types';
import { API } from '@shared/types/api';
import { HotKey, useHotkeyBinding, useShowHotkeyTooltips } from '@shared/util/hotkeys';
import { i18n } from '@shared/util/i18n';
import { SubformNavigationContext } from '../Subform/SubformEditField';
import { SubtableRow } from './useSubtableRowMenu';
import styles from './SubtableRowMenu.module.css';

const FIRST_FOCUSABLE_SELECTOR =
  ':is(input, textarea, button):not([disabled], [readonly], [type="hidden"])';

interface Props {
  workflowTemplate?: API.WorkflowTemplate;
  /** The card whose rows these are; a new row is created under it. */
  parentEntityId: string;
  isOpen: boolean;
  /** The row being edited; omit to add a new row. */
  row?: SubtableRow | null;
  /** Shows the row without Save, every column read-only (a read-only card). */
  isReadOnly?: boolean;
  /** The modal whose right edge the menu docks to on desktop. */
  containerAnchorName?: string;
  /** After a row is added or saved, before the menu closes. */
  onSaved: () => void;
  onClose: () => void;
}

/**
 * The overlay for adding or editing a subtable row: a form built from the subtable's Create or
 * Update view template.
 * On desktop it takes the chat sidebar's footprint in the surrounding modal; on mobile it's a
 * bottom sheet. Menu.Content unmounts when closed, so every open starts clean.
 */
export function SubtableRowMenu({
  workflowTemplate,
  parentEntityId,
  isOpen,
  row,
  isReadOnly = false,
  containerAnchorName,
  onSaved,
  onClose,
}: Props) {
  // With lightDismiss off the browser no longer closes the popover on Escape, so close it here.
  // On the panel rather than the form, so it also catches Escape while the panel itself has focus
  // (the focus trap's default). Capture phase, because the form's react-aria inputs stop Escape
  // from bubbling. Only for keys whose target is in the panel's own DOM: a nested popup (an open
  // select's listbox) portals elsewhere and keeps its Escape. preventDefault tells the
  // surrounding modal the Escape was handled (shouldCloseOnEscape), so it stays open. A form with
  // unsaved changes (data-dirty, set by RowFormLayout) swallows Escape without closing, so a stray
  // key press can't discard the edits; Close or Save still work.
  const closeOnEscape = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Escape' || !event.currentTarget.contains(event.target as Node)) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    if (isNil(event.currentTarget.querySelector('[data-dirty]'))) {
      onClose();
    }
  };

  return (
    <Menu isOpen={isOpen} onOpenChange={isNowOpen => !isNowOpen && onClose()} closeOnSelect={false}>
      <Menu.Content
        lightDismiss={false}
        mobileSheet
        className={styles.subtableRowMenu}
        vars={{ 'subtable-row-menu-container': containerAnchorName }}
        onKeyDownCapture={closeOnEscape}
      >
        {!isNil(workflowTemplate) && !isNil(row) && (
          <EditRowForm
            // Another row starts a fresh form.
            key={row.entityId}
            workflowTemplate={workflowTemplate}
            row={row}
            isReadOnly={isReadOnly}
            onSaved={onSaved}
            onClose={onClose}
          />
        )}
        {!isNil(workflowTemplate) && isNil(row) && (
          <NewRowForm
            workflowTemplate={workflowTemplate}
            parentEntityId={parentEntityId}
            onSaved={onSaved}
            onClose={onClose}
          />
        )}
      </Menu.Content>
    </Menu>
  );
}

interface RowFormProps {
  workflowTemplate: API.WorkflowTemplate;
  onSaved: () => void;
  onClose: () => void;
}

// Like subforms, a subtable only gets Create / Update view templates once someone customizes them.
// Without one, the form renders the template's components in order.

function NewRowForm({
  workflowTemplate,
  parentEntityId,
  onSaved,
  onClose,
}: RowFormProps & { parentEntityId: string }) {
  const viewTemplateId = workflowTemplate.viewTemplateDefaults?.create;
  const hasViewTemplate = !isNil(viewTemplateId);
  const {
    fields,
    updateField,
    components,
    errors,
    saveAttempted,
    viewTemplate,
    status,
    isDirty,
    onPressSave,
  } = useCreateCardEntity({
    workflowTemplateId: workflowTemplate.id,
    viewTemplateId,
    parentEntityId,
  });
  useCloseOnSuccess(status === 'success', onSaved, onClose);

  return (
    <RowFormLayout
      formType="CREATE"
      title={i18n('card.blocks.subtableInput.newRow')}
      workflowTemplateId={workflowTemplate.id}
      viewTemplateId={viewTemplate?.id}
      isLoading={isNil(fields) || (hasViewTemplate && isNil(viewTemplate))}
      isSaving={status === 'loading' && !isNil(fields)}
      saveLabel={i18n('common.add')}
      showSave
      isDirty={isDirty}
      isReadOnly={false}
      fields={fields}
      updateField={updateField}
      components={components}
      errors={errors}
      saveAttempted={saveAttempted}
      onSave={onPressSave}
    />
  );
}

function EditRowForm({
  workflowTemplate,
  row,
  isReadOnly,
  onSaved,
  onClose,
}: RowFormProps & { row: SubtableRow; isReadOnly: boolean }) {
  const viewTemplateId = workflowTemplate.viewTemplateDefaults?.update;
  const hasViewTemplate = !isNil(viewTemplateId);
  const {
    fields,
    updateField,
    components,
    errors,
    saveAttempted,
    viewTemplate,
    status,
    isSaving,
    isDirty,
    onPressSave,
  } = useEditCardEntity({
    entityId: row.entityId,
    workflowTemplateId: workflowTemplate.id,
    viewTemplateId,
    isReadOnly,
  });
  useCloseOnSuccess(status === 'success', onSaved, onClose);
  const { deleteRow, isDeleting } = useDeleteRow({
    workflowTemplateId: workflowTemplate.id,
    entityId: row.entityId,
    onDeleted: () => {
      onSaved();
      onClose();
    },
  });

  return (
    <RowFormLayout
      formType="EDIT"
      title={i18n('card.blocks.subtableInput.rowTitle', { number: row.number })}
      workflowTemplateId={workflowTemplate.id}
      viewTemplateId={viewTemplate?.id}
      isLoading={isNil(fields) || (hasViewTemplate && isNil(viewTemplate))}
      isSaving={isSaving}
      saveLabel={i18n('common.save')}
      // Only once the row has changed.
      showSave={!isReadOnly && isDirty}
      isDirty={isDirty}
      isReadOnly={isReadOnly}
      fields={fields}
      updateField={updateField}
      components={components}
      errors={errors}
      saveAttempted={saveAttempted}
      onSave={onPressSave}
      onDelete={isReadOnly ? undefined : deleteRow}
      isDeleting={isDeleting}
    />
  );
}

/** Once a save succeeds: tell the field to reload its rows, then close. */
function useCloseOnSuccess(isSuccess: boolean, onSaved: () => void, onClose: () => void) {
  // Once: the refetch re-renders the field, which hands down new callbacks before the menu closes.
  const hasClosedRef = useRef(false);
  useEffect(() => {
    if (isSuccess && !hasClosedRef.current) {
      hasClosedRef.current = true;
      onSaved();
      onClose();
    }
  }, [isSuccess, onSaved, onClose]);
}

/** Confirms, deletes the row, and toasts; onDeleted then refetches the rows and closes the menu. */
function useDeleteRow({
  workflowTemplateId,
  entityId,
  onDeleted,
}: {
  workflowTemplateId: string;
  entityId: string;
  onDeleted: () => void;
}) {
  const confirm = useConfirm();
  const toast = useToast();
  const deleteEntities = useDeleteEntitiesMutation(workflowTemplateId);
  const [isDeleting, setIsDeleting] = useState(false);

  const deleteRow = async () => {
    const confirmed = await confirm({
      title: i18n('card.blocks.subtableInput.deleteRowConfirmTitle'),
      message: i18n('card.blocks.subtableInput.deleteRowConfirmMessage'),
      confirmLabel: i18n('common.delete'),
      danger: true,
    });
    if (!confirmed) {
      return;
    }
    setIsDeleting(true);
    try {
      await deleteEntities([entityId]);
      toast({ message: i18n('card.blocks.subtableInput.rowDeleted') });
      onDeleted();
    } catch (error) {
      console.error(error);
      setIsDeleting(false);
    }
  };

  return { deleteRow, isDeleting };
}

function RowFormLayout({
  formType,
  title,
  workflowTemplateId,
  viewTemplateId,
  isLoading,
  isSaving,
  saveLabel,
  showSave,
  isDirty,
  isReadOnly,
  fields,
  updateField,
  components,
  errors,
  saveAttempted,
  onSave,
  onDelete,
  isDeleting = false,
}: {
  formType: 'CREATE' | 'EDIT';
  title: string;
  workflowTemplateId: string;
  viewTemplateId?: string;
  isLoading: boolean;
  isSaving: boolean;
  saveLabel: string;
  showSave: boolean;
  /** Unsaved changes: the menu ignores Escape (see closeOnEscape). */
  isDirty: boolean;
  isReadOnly: boolean;
  fields: FieldRecord | null;
  updateField: UpdateField;
  components: API.AnyComponent[];
  errors: CardEntityError[];
  saveAttempted: boolean;
  onSave: () => void;
  /** Shows a delete button below the form (an existing row on an editable card). */
  onDelete?: () => void;
  isDeleting?: boolean;
}) {
  // A read-only card shows its rows read-only too, the same way
  // applyComponentOverrides marks the card's own fields.
  const formComponents = useMemo(() => {
    if (isReadOnly) {
      return components.map(component => ({ ...component, readonly: true }));
    } else {
      return components;
    }
  }, [components, isReadOnly]);

  // AnchoredView focuses the first text input when the panel opens, but the fields arrive after
  // that, so focus the first focusable element once they've rendered. The focus trap leaves focus
  // that's already inside the panel alone, so this sticks.
  const [formElement, setFormElement] = useState<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!isLoading) {
      formElement?.querySelector<HTMLElement>(FIRST_FOCUSABLE_SELECTOR)?.focus();
    }
  }, [isLoading, formElement]);

  // The card's save hotkey, scoped to this form: registered on the form element, it only hears
  // keys pressed inside the menu, and the match stops propagation, so the card form's
  // document-level Save never sees them. Registered even while the button is hidden, so Mod+S on
  // an unchanged row does nothing rather than saving the card.
  const saveHotkey = useHotkeyBinding('saveCard');
  const [showHotkeyTooltips] = useShowHotkeyTooltips();
  const canSave = showSave && !isLoading && !isSaving;
  const onSaveHotkey = () => {
    if (canSave) {
      onSave();
    }
  };

  return (
    <View ref={setFormElement} className={styles.form} data-dirty={isDirty || undefined}>
      <HotKey
        hotkey={saveHotkey}
        callback={onSaveHotkey}
        enabled={!isNil(formElement)}
        options={{ target: formElement }}
      />
      <Menu.TitleBar
        title={title}
        startContent={<Menu.CloseButton />}
        endContent={
          showSave && (
            <Button
              variant="primary"
              size="medium"
              label={saveLabel}
              loading={isSaving}
              disabled={isLoading}
              onClick={onSave}
              tooltip={saveLabel}
              tooltipHotkey={saveHotkey}
              tooltipPosition="bottom"
              tooltipDisabled={isSaving || !showHotkeyTooltips}
            />
          )
        }
      />
      {isLoading && (
        <View className={styles.loading}>
          <LoadingIndicator />
        </View>
      )}
      {!isLoading && !isNil(fields) && (
        <FormScrollProvider>
          <FormScrollView className={styles.body}>
            <View className={styles.formContent}>
              <CardFormTypeContext.Provider value={formType}>
                <SubformNavigationContext
                  fields={fields}
                  updateField={updateField}
                  saveAttempted={saveAttempted}
                  apiErrors={errors}
                >
                  <EntityForm
                    fields={fields}
                    updateField={updateField}
                    components={formComponents}
                    errors={errors}
                    workflowTemplateId={workflowTemplateId}
                    viewTemplateId={viewTemplateId}
                  />
                </SubformNavigationContext>
              </CardFormTypeContext.Provider>
            </View>
            {!isNil(onDelete) && (
              <View center padding={1}>
                <Button
                  variant="secondary"
                  icon="trash"
                  danger
                  label={i18n('card.blocks.subtableInput.deleteRow')}
                  loading={isDeleting}
                  // Don't pass the promise to the button, so it remains enabled
                  // and can receive focus back when the confirm closes.
                  onClick={() => {
                    void onDelete();
                  }}
                />
              </View>
            )}
          </FormScrollView>
        </FormScrollProvider>
      )}
    </View>
  );
}
